// components/shared/sites/SiteForm.tsx

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Upload, X } from "lucide-react";
import { Site, SiteFormData } from "../../../types/features/Site";
import useLocales from "../../../hooks/features/locales/useLocales";

interface SiteFormProps {
  initialData?: Site;
  isEdit?: boolean;
  onSubmit: (data: SiteFormData) => Promise<void>;
  isLoading?: boolean;
  isSuccess?: boolean;
}

export default function SiteForm({
  initialData,
  isEdit = false,
  onSubmit,
  isLoading = false,
  isSuccess = false,
}: SiteFormProps) {
  const navigate = useNavigate();
  const { locales } = useLocales();

  const [formData, setFormData] = useState<SiteFormData>({
    slug: "",
    name: "",
    domain: "",
    logo: null,
    favicon: null,
    logoFile: null,
    faviconFile: null,
    default_locale: "fr",
    is_active: true,
  });

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        slug: initialData.slug,
        name: initialData.name,
        domain: initialData.domain || "",
        logo: initialData.logo || null,
        favicon: initialData.favicon || null,
        logoFile: null,
        faviconFile: null,
        default_locale: initialData.default_locale || "fr",
        is_active: initialData.is_active,
      });

      if (initialData.logo) setLogoPreview(initialData.logo);
      if (initialData.favicon) setFaviconPreview(initialData.favicon);
    }
  }, [initialData]);

  useEffect(() => {
    if (isSuccess) {
      setFormData({
        slug: "",
        name: "",
        domain: "",
        logo: null,
        favicon: null,
        logoFile: null,
        faviconFile: null,
        default_locale: "fr",
        is_active: true,
      });
      setLogoPreview(null);
      setFaviconPreview(null);
    }
  }, [isSuccess]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, field: 'logoFile' | 'faviconFile') => {
    const file = e.target.files?.[0];
    if (file) {
      // Créer une URL de prévisualisation
      const previewUrl = URL.createObjectURL(file);

      setFormData((prev) => ({
        ...prev,
        [field]: file,
      }));

      if (field === 'logoFile') {
        setLogoPreview(previewUrl);
      } else {
        setFaviconPreview(previewUrl);
      }
    }
    e.target.value = '';
  };

  const removeImage = (field: 'logo' | 'favicon', fileField: 'logoFile' | 'faviconFile') => {
    setFormData((prev) => ({
      ...prev,
      [field]: null,
      [fileField]: null,
    }));

    if (field === 'logo') {
      setLogoPreview(null);
    } else {
      setFaviconPreview(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Nettoyer les données avant envoi
    const submitData = { ...formData };

    // Supprimer les previews URL si elles existent
    if (submitData.logo === logoPreview && logoPreview?.startsWith('blob:')) {
      submitData.logo = null;
    }
    if (submitData.favicon === faviconPreview && faviconPreview?.startsWith('blob:')) {
      submitData.favicon = null;
    }

    await onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* Informations générales */}
        <div className="mb-6">
          <div className="border-b border-gray-200 mb-4 pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Informations générales</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du site <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                placeholder="Mon Site Web"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Slug <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                placeholder="mon-site"
              />
              <p className="text-xs text-gray-500 mt-1">
                Identifiant unique du site (ex: "izi-call", "izilife-pay")
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Domaine
              </label>
              <input
                type="text"
                name="domain"
                value={formData.domain}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                placeholder="exemple.com"
              />
            </div>
          </div>
        </div>

        {/* Logo & Favicon avec upload */}
        <div className="mb-6">
          <div className="border-b border-gray-200 mb-4 pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Apparence</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Logo
              </label>
              {logoPreview ? (
                <div className="relative inline-block">
                  <img
                    src={logoPreview}
                    alt="Logo"
                    className="w-32 h-32 object-contain border rounded-lg p-2 bg-gray-50"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage('logo', 'logoFile')}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                      <Upload className="w-4 h-4" />
                      Choisir un logo
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileSelect(e, 'logoFile')}
                      className="hidden"
                    />
                  </label>
                  <span className="text-xs text-gray-400">ou</span>
                  <input
                    type="url"
                    name="logo"
                    value={formData.logo || ''}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm"
                    placeholder="https://exemple.com/logo.png"
                  />
                </div>
              )}
            </div>

            {/* Favicon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Favicon
              </label>
              {faviconPreview ? (
                <div className="relative inline-block">
                  <img
                    src={faviconPreview}
                    alt="Favicon"
                    className="w-16 h-16 object-contain border rounded-lg p-2 bg-gray-50"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage('favicon', 'faviconFile')}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                      <Upload className="w-4 h-4" />
                      Choisir un favicon
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileSelect(e, 'faviconFile')}
                      className="hidden"
                    />
                  </label>
                  <span className="text-xs text-gray-400">ou</span>
                  <input
                    type="url"
                    name="favicon"
                    value={formData.favicon || ''}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm"
                    placeholder="https://exemple.com/favicon.ico"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Langue et statut */}
        <div className="mb-6">
          <div className="border-b border-gray-200 mb-4 pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Configuration</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Langue par défaut <span className="text-red-500">*</span>
              </label>
              <select
                name="default_locale"
                value={formData.default_locale}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
              >
                {locales.map((locale) => (
                  <option key={locale.code} value={locale.code}>
                    {locale.flag} {locale.name} ({locale.code.toUpperCase()})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleCheckbox}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm font-medium text-gray-700">Site actif</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          <Save className="w-4 h-4" />
          {isLoading ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer"}
        </button>
      </div>
    </form>
  );
}